import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Alert,
  AlertTitle,
  Chip,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import { useMutation, useQuery, useQueryClient } from "react-query";
import client from "../../../API";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const NotifSettings = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Fetch email settings
  const { data, isFetching, error } = useQuery(
    ["emailSettings"],
    () => client.users.fetchEmailSettings(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (err) => {
        console.error("Error fetching email settings:", err);
        toast.error("Failed to load email settings");
      },
    }
  );

  // ====================================================================
  // STATE - ALL ENABLED BY DEFAULT
  // ====================================================================
  
  // State for each category - will be populated with full lists from API
  const [systemSettings, setSystemSettings] = useState({
    enabled_types: [],
    frequency: "immediate",
    specific: {
      timeline_notifications: true,
      deliverable_reminders: true,
      unread_message_reminders: true,
      conversation_digests: true,
    },
    digest_frequencies: {
      unread_messages: "daily",
      conversations: "weekly",
    },
  });

  const [marketingSettings, setMarketingSettings] = useState({
    enabled_types: [], // Will be populated with all types from API
    frequency: "weekly",
    specific: {
      weekly_newsletter: true,  // Changed from false to true
      feature_announcements: true,  // Changed from false to true
      inactive_reminders: true,  // Changed from false to true
    },
  });

  const [maintenanceSettings, setMaintenanceSettings] = useState({
    enabled_types: [],
    specific: {
      reauth_notifications: true,
      profile_reminders: true,
      security_alerts: true,
    },
  });

  // ====================================================================
  // LOAD DATA - DEFAULT TO ALL ENABLED IF EMPTY
  // ====================================================================
  
  useEffect(() => {
    if (!data) return;

    // System emails - default to all available types if none specified
    if (data.system) {
      setSystemSettings({
        enabled_types: data.system.enabled_types?.length 
          ? data.system.enabled_types 
          : data.system.available_types || [],  // Use all if empty
        frequency: data.system.frequency || "immediate",
        specific: data.system.specific_settings || {
          timeline_notifications: true,
          deliverable_reminders: true,
          unread_message_reminders: true,
          conversation_digests: true,
        },
        digest_frequencies: data.system.digest_frequencies || {
          unread_messages: "daily",
          conversations: "weekly",
        },
      });
    }

    // Marketing emails - default to all available types if none specified
    // THIS IS THE KEY CHANGE: No longer empty by default
    if (data.marketing) {
      setMarketingSettings({
        enabled_types: data.marketing.enabled_types?.length 
          ? data.marketing.enabled_types 
          : data.marketing.available_types || [],  // Use all if empty
        frequency: data.marketing.frequency || "weekly",
        specific: data.marketing.specific_settings || {
          weekly_newsletter: true,
          feature_announcements: true,
          inactive_reminders: true,
        },
      });
    }

    // Maintenance emails - default to all available types if none specified
    if (data.maintenance) {
      setMaintenanceSettings({
        enabled_types: data.maintenance.enabled_types?.length 
          ? data.maintenance.enabled_types 
          : data.maintenance.available_types || [],  // Use all if empty
        specific: data.maintenance.specific_settings || {
          reauth_notifications: true,
          profile_reminders: true,
          security_alerts: true,
        },
      });
    }
  }, [data]);

  // ====================================================================
  // HELPER FUNCTIONS
  // ====================================================================

  // Helper to format email type labels
  const formatLabel = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Check if all items in a category are selected
  const isAllSelected = (category) => {
    const categoryData = data?.[category];
    if (!categoryData?.available_types) return false;

    const currentSettings = {
      system: systemSettings,
      marketing: marketingSettings,
      maintenance: maintenanceSettings
    }[category];

    return currentSettings?.enabled_types?.length === categoryData.available_types.length;
  };

  // Toggle email type in category
  const toggleEmailType = (category, emailType) => {
    if (category === "system") {
      setSystemSettings((prev) => ({
        ...prev,
        enabled_types: prev.enabled_types.includes(emailType)
          ? prev.enabled_types.filter((t) => t !== emailType)
          : [...prev.enabled_types, emailType],
      }));
    } else if (category === "marketing") {
      setMarketingSettings((prev) => ({
        ...prev,
        enabled_types: prev.enabled_types.includes(emailType)
          ? prev.enabled_types.filter((t) => t !== emailType)
          : [...prev.enabled_types, emailType],
      }));
    } else if (category === "maintenance") {
      setMaintenanceSettings((prev) => ({
        ...prev,
        enabled_types: prev.enabled_types.includes(emailType)
          ? prev.enabled_types.filter((t) => t !== emailType)
          : [...prev.enabled_types, emailType],
      }));
    }
  };

  // Toggle specific setting
  const toggleSpecificSetting = (category, settingKey) => {
    if (category === "system") {
      setSystemSettings((prev) => ({
        ...prev,
        specific: {
          ...prev.specific,
          [settingKey]: !prev.specific[settingKey],
        },
      }));
    } else if (category === "marketing") {
      setMarketingSettings((prev) => ({
        ...prev,
        specific: {
          ...prev.specific,
          [settingKey]: !prev.specific[settingKey],
        },
      }));
    } else if (category === "maintenance") {
      setMaintenanceSettings((prev) => ({
        ...prev,
        specific: {
          ...prev.specific,
          [settingKey]: !prev.specific[settingKey],
        },
      }));
    }
  };

  // Select/Unselect all for a category
  const toggleAllInCategory = (category) => {
    if (category === "system" && data?.system?.available_types) {
      const allTypes = data.system.available_types;
      setSystemSettings((prev) => ({
        ...prev,
        enabled_types:
          prev.enabled_types.length === allTypes.length ? [] : [...allTypes],
      }));
    } else if (category === "marketing" && data?.marketing?.available_types) {
      const allTypes = data.marketing.available_types;
      setMarketingSettings((prev) => ({
        ...prev,
        enabled_types:
          prev.enabled_types.length === allTypes.length ? [] : [...allTypes],
      }));
    } else if (category === "maintenance" && data?.maintenance?.available_types) {
      const allTypes = data.maintenance.available_types;
      setMaintenanceSettings((prev) => ({
        ...prev,
        enabled_types:
          prev.enabled_types.length === allTypes.length ? [] : [...allTypes],
      }));
    }
  };

  // ====================================================================
  // SAVE MUTATION
  // ====================================================================

  const saveMutation = useMutation(
    (payload) => client.users.updateEmailSettings(payload),
    {
      onSuccess: () => {
        toast.success("Settings saved successfully");
        qc.invalidateQueries("emailSettings");
      },
      onError: (err) => {
        console.error("Save error:", err);
        toast.error("Failed to save settings");
      },
    }
  );

  const handleSave = () => {
    const payload = {
      system: {
        enabled_types: systemSettings.enabled_types,
        frequency: systemSettings.frequency,
        specific_settings: systemSettings.specific,
        digest_frequencies: systemSettings.digest_frequencies,
      },
      marketing: {
        enabled_types: marketingSettings.enabled_types,
        frequency: marketingSettings.frequency,
        specific_settings: marketingSettings.specific,
      },
      maintenance: {
        enabled_types: maintenanceSettings.enabled_types,
        specific_settings: maintenanceSettings.specific,
      },
    };

    saveMutation.mutate(payload);
  };



  if (error) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Alert severity="error">
            Error loading settings: {error.message}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (isFetching && !data) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // ====================================================================
  // MAIN RENDER
  // ====================================================================

  return (
    <Box>
      <CardContent>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
          Email Notification Preferences
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Customize which emails you receive from Blitz
        </Typography>

        {/* INFO ALERT - NEW */}
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ mb: 4 }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>Default Settings</AlertTitle>
          All email notifications are <strong>enabled by default</strong> to ensure you stay informed about important updates, 
          campaigns, and opportunities. You can customize or disable any categories below based on your preferences.
        </Alert>

        <Grid container spacing={4}>
          {/* ============================================================ */}
          {/* TRANSACTIONAL EMAILS */}
          {/* ============================================================ */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <LockIcon fontSize="small" color="action" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Transactional Emails
                </Typography>
                <Chip label="Always On" size="small" color="primary" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Critical business emails that cannot be disabled
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {data?.transactional?.types?.map((type) => (
                  <Chip
                    key={type}
                    label={formatLabel(type)}
                    size="small"
                    variant="outlined"
                    icon={<CheckCircleIcon />}
                    color="success"
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* ============================================================ */}
          {/* SYSTEM NOTIFICATIONS */}
          {/* ============================================================ */}
          <Grid item xs={12}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
            >
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    System Notifications
                  </Typography>
                  {isAllSelected("system") && (
                    <Chip 
                      icon={<CheckCircleIcon />} 
                      label="All Enabled" 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Automated notifications about your account activity
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => toggleAllInCategory("system")}
                variant={isAllSelected("system") ? "outlined" : "contained"}
              >
                {isAllSelected("system") ? "Unselect All" : "Select All"}
              </Button>
            </Box>

            <FormGroup sx={{ mb: 2 }}>
              {data?.system?.available_types?.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={systemSettings.enabled_types.includes(type)}
                      onChange={() => toggleEmailType("system", type)}
                      color="success"
                    />
                  }
                  label={formatLabel(type)}
                />
              ))}
            </FormGroup>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Delivery Frequency"
                  value={systemSettings.frequency}
                  onChange={(e) =>
                    setSystemSettings((prev) => ({
                      ...prev,
                      frequency: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="immediate">Immediate</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="never">Never</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Unread Messages Digest"
                  value={systemSettings.digest_frequencies.unread_messages}
                  onChange={(e) =>
                    setSystemSettings((prev) => ({
                      ...prev,
                      digest_frequencies: {
                        ...prev.digest_frequencies,
                        unread_messages: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="never">Never</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Conversation Digest"
                  value={systemSettings.digest_frequencies.conversations}
                  onChange={(e) =>
                    setSystemSettings((prev) => ({
                      ...prev,
                      digest_frequencies: {
                        ...prev.digest_frequencies,
                        conversations: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="never">Never</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* ============================================================ */}
          {/* MARKETING EMAILS */}
          {/* ============================================================ */}
          <Grid item xs={12}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
            >
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Marketing Emails
                  </Typography>
                  {isAllSelected("marketing") && (
                    <Chip 
                      icon={<CheckCircleIcon />} 
                      label="All Enabled" 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Newsletters, updates, and promotional content
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => toggleAllInCategory("marketing")}
                variant={isAllSelected("marketing") ? "outlined" : "contained"}
              >
                {isAllSelected("marketing") ? "Unselect All" : "Select All"}
              </Button>
            </Box>

            <FormGroup sx={{ mb: 2 }}>
              {data?.marketing?.available_types?.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={marketingSettings.enabled_types.includes(type)}
                      onChange={() => toggleEmailType("marketing", type)}
                      color="success"
                    />
                  }
                  label={formatLabel(type)}
                />
              ))}
            </FormGroup>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Delivery Frequency"
                  value={marketingSettings.frequency}
                  onChange={(e) =>
                    setMarketingSettings((prev) => ({
                      ...prev,
                      frequency: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="immediate">Immediate</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="never">Never</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* ============================================================ */}
          {/* MAINTENANCE EMAILS */}
          {/* ============================================================ */}
          <Grid item xs={12}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
            >
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Maintenance & Security
                  </Typography>
                  {isAllSelected("maintenance") && (
                    <Chip 
                      icon={<CheckCircleIcon />} 
                      label="All Enabled" 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Account health, security alerts, and system updates
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => toggleAllInCategory("maintenance")}
                variant={isAllSelected("maintenance") ? "outlined" : "contained"}
              >
                {isAllSelected("maintenance") ? "Unselect All" : "Select All"}
              </Button>
            </Box>

            <FormGroup>
              {data?.maintenance?.available_types?.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={maintenanceSettings.enabled_types.includes(type)}
                      onChange={() => toggleEmailType("maintenance", type)}
                      color="success"
                    />
                  }
                  label={formatLabel(type)}
                />
              ))}
            </FormGroup>
          </Grid>

          {/* ============================================================ */}
          {/* STATUS SUMMARY - NEW */}
          {/* ============================================================ */}
          <Grid item xs={12}>
            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle sx={{ fontWeight: 600 }}>Current Status</AlertTitle>
              <Typography variant="body2">
                <strong>System:</strong> {systemSettings?.enabled_types?.length || 0} of {data?.system?.available_types?.length || 0} enabled • {' '}
                <strong>Marketing:</strong> {marketingSettings?.enabled_types?.length || 0} of {data?.marketing?.available_types?.length || 0} enabled • {' '}
                <strong>Maintenance:</strong> {maintenanceSettings?.enabled_types?.length || 0} of {data?.maintenance?.available_types?.length || 0} enabled
              </Typography>
            </Alert>
          </Grid>

          {/* ============================================================ */}
          {/* ACTION BUTTONS */}
          {/* ============================================================ */}
          <Grid item xs={12} sx={{ textAlign: "right", mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saveMutation.isLoading}
              sx={{ minWidth: 150 }}
            >
              {saveMutation.isLoading ? (
                <CircularProgress size={24} />
              ) : (
                "Save Preferences"
              )}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="text"
              color="error"
              onClick={() => navigate("/delete_account")}
            >
              Delete Account
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Box>
  );
};

export default NotifSettings;
export const NotifTabIcon = NotificationsIcon;